/**
 * ButtonGroup Component
 *
 * A group of buttons where only one can be selected at a time (radio button behavior).
 * Provides a clean interface for single-choice selections with proper accessibility support.
 *
 * @module components/ui/ButtonGroup
 */

import React, { forwardRef } from 'react';
import { cn } from '@/utils/cn';
import { useTheme } from '@/hooks/useTheme';
import { FormLabel } from './Label';

/**
 * Option interface for ButtonGroup
 */
export interface ButtonGroupOption {
  /** The value to be returned when this option is selected */
  value: string | number;
  /** The display label for this option */
  label: string;
  /** Optional description text shown below the label */
  description?: string;
  /** Whether this option is disabled */
  disabled?: boolean;
}

/**
 * ButtonGroup component props interface
 */
export interface ButtonGroupProps {
  /** Array of options to display as buttons */
  options: ButtonGroupOption[];
  /** Currently selected value */
  value?: string | number;
  /** Callback fired when selection changes */
  onChange?: (value: string | number) => void;
  /** Label for the button group */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the entire group is disabled */
  disabled?: boolean;
  /** Error message to display */
  error?: string;
  /** Additional CSS classes */
  className?: string;
  /** Name attribute for the radio group */
  name?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Layout orientation */
  orientation?: 'horizontal' | 'vertical';
  /** Whether to show descriptions */
  showDescriptions?: boolean;
  /** Maximum number of columns on desktop screens (for horizontal orientation) */
  maxColumns?: number;
}

/**
 * ButtonGroup component for single-choice selections
 *
 * @example
 * ```tsx
 * <ButtonGroup
 *   label="Budget Range"
 *   options={[
 *     { value: 5, label: 'Low budget', description: '$2 - $5 per serving' },
 *     { value: 8, label: 'Moderate budget', description: '$5-$8 per serving' }
 *   ]}
 *   value={selectedBudget}
 *   onChange={setBudget}
 *   required
 * />
 * ```
 */
const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupProps>(
  ({
    options,
    value,
    onChange,
    label,
    required = false,
    disabled = false,
    error,
    className,
    name,
    size = 'md',
    orientation = 'vertical',
    showDescriptions = true,
    maxColumns = 4,
    ...props
  }, ref) => {
    const { isDark } = useTheme();

    // Generate a unique name if not provided
    const groupName = name || `button-group-${Math.random().toString(36).substr(2, 9)}`;

    // Size classes
    const sizeClasses = {
      sm: 'text-sm px-3 py-2',
      md: 'text-base px-4 py-3',
      lg: 'text-lg px-6 py-4'
    };

    // Layout classes
    const getLayoutClasses = () => {
      if (orientation === 'vertical') {
        return 'space-y-2';
      }

      // For horizontal orientation, create a responsive grid based on maxColumns.
      // Class names MUST be static strings — Tailwind's scanner can't see an
      // interpolated `grid-cols-${n}`, so those classes get purged and the grid
      // silently collapses to a single column. maxColumns is clamped to 1–6.
      const cols = Math.min(Math.max(Math.round(maxColumns), 1), 6);
      const baseGridCols: Record<number, string> = {
        1: 'grid-cols-1',
        2: 'grid-cols-2',
        3: 'grid-cols-3',
      };
      const lgGridCols: Record<number, string> = {
        1: 'lg:grid-cols-1',
        2: 'lg:grid-cols-2',
        3: 'lg:grid-cols-3',
        4: 'lg:grid-cols-4',
        5: 'lg:grid-cols-5',
        6: 'lg:grid-cols-6',
      };
      return cn('grid gap-2', baseGridCols[Math.ceil(cols / 2)], 'sm:grid-cols-2', lgGridCols[cols]);
    };

    const handleOptionChange = (optionValue: string | number) => {
      if (!disabled && onChange) {
        onChange(optionValue);
      }
    };

    const getButtonClasses = (option: ButtonGroupOption, isSelected: boolean) => {
      const baseClasses = cn(
        'relative flex items-center justify-center w-full rounded-lg border-2 transition-all duration-200 cursor-pointer focus-within:ring-2 focus-within:ring-offset-2',
        sizeClasses[size],
        orientation === 'horizontal' ? 'text-center' : 'items-start'
      );

      if (disabled || option.disabled) {
        return cn(
          baseClasses,
          'cursor-not-allowed opacity-50',
          isDark
            ? 'border-secondary-700 bg-secondary-800 text-secondary-500'
            : 'border-secondary-200 bg-secondary-50 text-secondary-400'
        );
      }

      if (isSelected) {
        return cn(
          baseClasses,
          'focus-within:ring-primary-500/50',
          isDark
            ? 'border-primary-500 bg-primary-900/20 text-primary-300'
            : 'border-primary-500 bg-primary-50 text-primary-700'
        );
      }

      return cn(
        baseClasses,
        'hover:border-primary-300 focus-within:ring-primary-500/50',
        isDark
          ? 'border-gray-500 bg-secondary-800 text-secondary-300 hover:bg-secondary-700'
          : 'border-gray-500 bg-white text-secondary-700 hover:bg-secondary-50'
      );
    };

    return (
      <div ref={ref} className={cn('space-y-2', className)} {...props}>
        {/* Label */}
        {label && (
          <FormLabel
            htmlFor={`${groupName}-group`}
            required={required}
            error={!!error}
            disabled={disabled}
          >
            {label}
          </FormLabel>
        )}

        {/* Button Group */}
        <div
          role="radiogroup"
          aria-labelledby={label ? `${groupName}-label` : undefined}
          aria-required={required}
          aria-invalid={!!error}
          className={getLayoutClasses()}
        >
          {options.map((option, index) => {
            const isSelected = value === option.value;
            const optionId = `${groupName}-option-${index}`;

            return (
              <label
                key={option.value}
                htmlFor={optionId}
                className={getButtonClasses(option, isSelected)}
              >
                {/* Hidden Radio Input */}
                <input
                  type="radio"
                  id={optionId}
                  name={groupName}
                  value={option.value}
                  checked={isSelected}
                  onChange={() => handleOptionChange(option.value)}
                  disabled={disabled || option.disabled}
                  className="sr-only"
                  aria-describedby={
                    option.description && showDescriptions
                      ? `${optionId}-description`
                      : undefined
                  }
                />

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {orientation === 'horizontal' ? (
                    // Horizontal layout - no radio indicator, centered content
                    <div className="text-center">
                      <div className={cn(
                        'font-medium',
                        isSelected
                          ? isDark ? 'text-primary-300' : 'text-primary-700'
                          : isDark ? 'text-secondary-300' : 'text-secondary-700'
                      )}>
                        {option.label}
                      </div>

                      {/* Description */}
                      {option.description && showDescriptions && (
                        <div
                          id={`${optionId}-description`}
                          className={cn(
                            'text-xs mt-1',
                            isSelected
                              ? isDark ? 'text-primary-400' : 'text-primary-600'
                              : isDark ? 'text-secondary-400' : 'text-secondary-500'
                          )}
                        >
                          {option.description}
                        </div>
                      )}
                    </div>
                  ) : (
                    // Vertical layout - with radio indicator
                    <div className="flex items-center">
                      {/* Radio Indicator */}
                      <div
                        className={cn(
                          'flex-shrink-0 w-4 h-4 rounded-full border-2 mr-3 transition-all duration-200',
                          isSelected
                            ? isDark
                              ? 'border-primary-500 bg-primary-500'
                              : 'border-primary-500 bg-primary-500'
                            : isDark
                              ? 'border-secondary-500'
                              : 'border-secondary-400'
                        )}
                      >
                        {isSelected && (
                          <div className="w-full h-full rounded-full bg-white scale-50" />
                        )}
                      </div>

                      {/* Label */}
                      <div className="flex-1 min-w-0">
                        <div className={cn(
                          'font-medium',
                          isSelected
                            ? isDark ? 'text-primary-300' : 'text-primary-700'
                            : isDark ? 'text-secondary-300' : 'text-secondary-700'
                        )}>
                          {option.label}
                        </div>

                        {/* Description */}
                        {option.description && showDescriptions && (
                          <div
                            id={`${optionId}-description`}
                            className={cn(
                              'text-sm mt-1',
                              isSelected
                                ? isDark ? 'text-primary-400' : 'text-primary-600'
                                : isDark ? 'text-secondary-400' : 'text-secondary-500'
                            )}
                          >
                            {option.description}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        {/* Error Message */}
        {error && (
          <div className={cn(
            'text-sm mt-1',
            isDark ? 'text-red-400' : 'text-red-600'
          )}>
            {error}
          </div>
        )}
      </div>
    );
  }
);

ButtonGroup.displayName = 'ButtonGroup';

export { ButtonGroup };